import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  StatusChip,
  Typography,
  palette,
  type StatusKind,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { VisitDetail, VisitFix, VisitStatus } from '@munifocus/contracts-gov';
import { useVisits } from '../lib/visits-api';
import { frenchFirstOfMonth } from '@munifocus/utils';

const STATUS_KIND: Record<VisitStatus, StatusKind> = {
  en_route: 'pending',
  on_site: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

const STEPS = [
  ['enRoute', 'en_route'],
  ['onSite', 'on_site'],
  ['completed', 'completed'],
  ['cancelled', 'cancelled'],
] as const;

function formatDateTime(iso: string, locale: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso)),
  );
}

function FixLine({ label, fix, locale }: { label: string; fix: VisitFix; locale: string }) {
  const { t } = useTranslation();
  const where =
    fix.lat === null
      ? t('gov.visits.noFix')
      : fix.accuracy !== null
        ? t('gov.visits.accuracy', { meters: Math.round(fix.accuracy) })
        : t('gov.visits.fixed');
  return (
    <Stack
      component="li"
      direction="row"
      sx={{ justifyContent: 'space-between', gap: 2, py: 0.25 }}
    >
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
        {formatDateTime(fix.at, locale)} · {where}
      </Typography>
    </Stack>
  );
}

function VisitRow({ visit, locale }: { visit: VisitDetail; locale: string }) {
  const { t } = useTranslation();
  return (
    <Box
      component="li"
      sx={{ py: 1.25, pl: 1.5, borderLeft: `2px solid ${palette.border.line}`, listStyle: 'none' }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {visit.officerName ?? t('gov.visits.unknownOfficer')}
        </Typography>
        <StatusChip
          status={STATUS_KIND[visit.status]}
          label={t(`gov.visits.status.${visit.status}`)}
        />
      </Stack>
      <Box component="ul" sx={{ m: 0, mt: 0.5, p: 0, listStyle: 'none' }}>
        {STEPS.map(([field, status]) => {
          const fix = visit[field];
          return fix ? (
            <FixLine
              key={status}
              label={t(`gov.visits.step.${status}`)}
              fix={fix}
              locale={locale}
            />
          ) : null;
        })}
      </Box>
      {visit.route && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(visit.route.estimated ? 'gov.visits.plannedEstimated' : 'gov.visits.planned', {
            km: (visit.route.distanceMeters / 1000).toLocaleString(locale, {
              maximumFractionDigits: 1,
            }),
            time: formatDateTime(visit.route.eta, locale),
          })}
        </Typography>
      )}
      {visit.notes && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {visit.notes}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Every visit made to the file's property, newest first: who went, when they
 * set off, arrived and finished, and how good the device's fix was at each tap.
 * Recorded from the phone; read here.
 */
export function VisitTimeline({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useVisits(fileId);

  return (
    <Box component="section" aria-labelledby="visit-timeline-title">
      <Typography id="visit-timeline-title" variant="overline">
        {t('gov.visits.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('gov.visits.description')}
      </Typography>
      {isLoading && <CircularProgress size={20} />}
      {isError && <Alert severity="error">{t('gov.visits.loadError')}</Alert>}
      {data && data.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.visits.empty')}
        </Typography>
      )}
      {data && data.length > 0 && (
        <Box component="ul" sx={{ m: 0, p: 0 }}>
          {data.map((v) => (
            <VisitRow key={v.id} visit={v} locale={i18n.language} />
          ))}
        </Box>
      )}
    </Box>
  );
}
